/**
 * O que o modulo 01 tem: os seis cadastros de estrutura, a governanca e a
 * trilha.
 *
 * ## "Em uso" e "Arquivado", e nunca "Ativo" / "Inativo"
 *
 * A coluna do banco se chama `ativo`, e a tela NAO a escreve com esse nome. O
 * manual e explicito (p.23, e o CLAUDE.md repete): "Ativo", "Ocioso" e
 * "Inativo" nao existem no sistema. Os seis status oficiais descrevem MAQUINA
 * (`Operando`, `Standby`, `Parada`, `Em manutenção`, `Disponível`, `Em
 * deslocamento`) e nao servem para um cadastro — um cliente nao opera nem fica
 * em standby. Entao o cadastro tem SITUACAO, com duas palavras que nao colidem
 * com o vocabulario de maquina e que dizem o que a lista precisa dizer:
 *
 *   `Em uso`     — aparece nas listas de escolha de toda tela do sistema
 *   `Arquivado`  — nao aparece mais para escolher, e a historia dele fica
 *
 * Arquivar e o mecanismo previsto no lugar de apagar: apagar um cliente
 * cascatearia contratos e obras, e reescrever o passado e o oposto da
 * ferramenta de auditoria.
 */

/** Os seis cadastros de estrutura. A chave e o caminho da rota. */
export const CADASTROS = [
  'filiais',
  'clientes',
  'fornecedores',
  'centros-de-custo',
  'obras',
  'contratos',
] as const;

export type TipoDeCadastro = (typeof CADASTROS)[number];

export function ehTipoDeCadastro(valor: unknown): valor is TipoDeCadastro {
  return typeof valor === 'string' && (CADASTROS as readonly string[]).includes(valor);
}

/**
 * Como cada cadastro se chama na tela, no singular e no plural.
 *
 * `botao` e o nome no ROTULO DE BOTAO, que e verbo + objeto em no maximo tres
 * palavras (p.15): "Cadastrar centro de custo" passa do limite, entao o botao
 * diz "Cadastrar centro" — o seletor e o titulo da janela dizem de que centro.
 *
 * `genero` e o do nome no singular, e `generoDoPlural` o do plural: as frases
 * da tela concordam com ele (p.22–23: o texto da tela e a marca falando). Com
 * artigo, pronome e participio fixos no masculino, a tela escrevia "Nenhum
 * obra cadastrado" e "Filiais cadastrados". "Obras e locais" e plural misto, e
 * por isso masculino.
 */
export const NOME_DO_CADASTRO: Record<
  TipoDeCadastro,
  {
    readonly um: string;
    readonly muitos: string;
    readonly botao: string;
    readonly genero: 'm' | 'f';
    readonly generoDoPlural: 'm' | 'f';
  }
> = {
  filiais: { um: 'filial', muitos: 'Filiais', botao: 'filial', genero: 'f', generoDoPlural: 'f' },
  clientes: { um: 'cliente', muitos: 'Clientes', botao: 'cliente', genero: 'm', generoDoPlural: 'm' },
  fornecedores: { um: 'fornecedor', muitos: 'Fornecedores', botao: 'fornecedor', genero: 'm', generoDoPlural: 'm' },
  'centros-de-custo': {
    um: 'centro de custo',
    muitos: 'Centros de custo',
    botao: 'centro',
    genero: 'm',
    generoDoPlural: 'm',
  },
  obras: { um: 'obra', muitos: 'Obras e locais', botao: 'obra', genero: 'f', generoDoPlural: 'm' },
  contratos: { um: 'contrato', muitos: 'Contratos', botao: 'contrato', genero: 'm', generoDoPlural: 'm' },
};

/**
 * As palavras que concordam com o nome do cadastro: "Nenhuma filial
 * cadastrada", "Cadastre a primeira obra", "Ela sai das listas".
 */
export function flexaoDe(tipo: TipoDeCadastro) {
  const nome = NOME_DO_CADASTRO[tipo];
  const f = nome.genero === 'f';
  return {
    /** Artigo definido: "tira a obra". */
    artigo: f ? 'a' : 'o',
    nenhum: f ? 'Nenhuma' : 'Nenhum',
    primeiro: f ? 'a primeira' : 'o primeiro',
    ele: f ? 'Ela' : 'Ele',
    comEle: f ? 'com ela' : 'com ele',
    cadastrado: f ? 'cadastrada' : 'cadastrado',
    cadastrados: nome.generoDoPlural === 'f' ? 'cadastradas' : 'cadastrados',
  };
}

/** O recurso como o trigger de auditoria o escreve: `<schema>.<tabela>`. */
export const RECURSO_DO_CADASTRO: Record<TipoDeCadastro, string> = {
  filiais: 'org.filial',
  clientes: 'org.cliente',
  fornecedores: 'org.fornecedor',
  'centros-de-custo': 'org.centro_custo',
  obras: 'org.obra',
  contratos: 'org.contrato',
};

/**
 * Uma linha de cadastro, como a API a devolve.
 *
 * Os campos extras (`cliente_nome`, `inicio`, `tipo`) so existem em alguns dos
 * seis, e por isso sao opcionais. A alternativa — seis interfaces e seis
 * tabelas — daria seis telas para manter e seis lugares para a regra da
 * situacao divergir.
 */
export interface LinhaDeCadastro {
  readonly id: string;
  readonly codigo: string;
  /** `contratos` usa `descricao` no lugar de `nome`; a tela resolve os dois. */
  readonly nome?: string;
  readonly descricao?: string;
  readonly ativo: boolean;
  /** 0109: a gravação ficou em contingência (o ERP é o mestre). Só nos cadastros que ele mestra. */
  readonly emContingencia?: boolean;
  readonly criado_em: string;
  readonly atualizado_em?: string;
  /**
   * A versao da linha (backend/src/http/versao-da-ficha.ts). O formulario a
   * guarda AO ABRIR e a devolve no envio: o servidor recusa com 409
   * `ficha_alterada` se outra pessoa gravou depois (robos de 20/10/2026).
   */
  readonly versao?: string | null;
  readonly municipio?: string;
  readonly uf?: string | null;
  readonly documento?: string | null;
  readonly tipo?: string;
  readonly cliente_id?: string | null;
  readonly cliente_nome?: string | null;
  readonly centro_custo_id?: string | null;
  readonly centro_custo_nome?: string | null;
  readonly filial_id?: string | null;
  readonly filial_nome?: string | null;
  readonly inicio?: string | null;
  readonly fim?: string | null;
}

export interface Papel {
  readonly id: string;
  readonly codigo: string;
  readonly nome: string;
  readonly descricao: string;
  readonly ativo: boolean;
  readonly permissoes: number;
  readonly pessoas: number;
  readonly criado_em: string;
  /**
   * A versao da linha (backend/src/http/versao-da-ficha.ts). O formulario a
   * guarda AO ABRIR e a devolve no envio: o servidor recusa com 409
   * `ficha_alterada` se outra pessoa gravou depois (robos de 20/10/2026).
   */
  readonly versao?: string | null;
  /**
   * 0128 · A TRAVA DO LANÇAMENTO DE ABASTECIMENTO do papel: os tipos e os
   * tanques que quem o tem lança. Nulo = sem trava daquilo. Ausentes no
   * servidor de antes, e aí o papel não tem trava.
   */
  readonly abastecimento_tipos?: readonly ('interno' | 'externo')[] | null;
  readonly abastecimento_tanques?: readonly string[] | null;
  /** O papel lança abastecimento (tem `abastecimento.registrar`): só ele tem a trava. */
  readonly lanca_abastecimento?: boolean;
  /**
   * 0132 · Papel de posto parceiro: gente de fora da empresa. Só as três
   * permissões do lançamento, só o externo, e a concessão exige o fornecedor.
   * Ausente em resposta antiga é papel da empresa.
   */
  readonly parceiro?: boolean;
  /** 0132 · O fornecedor do papel de UM posto; nulo quando é escolhido na concessão. */
  readonly fornecedor_id?: string | null;
  readonly fornecedor_nome?: string | null;
  /** 0130 · O início do app de quem tem este papel; ausente em resposta antiga é o painel. */
  readonly inicio_do_app?: 'padrao' | 'abastecimento';
}

export interface Permissao {
  readonly id: string;
  readonly codigo: string;
  readonly descricao: string;
  readonly modulo: string;
}

export interface PermissaoDoPapel extends Permissao {
  readonly permissao_id: string;
}

export interface Pessoa {
  readonly id: string;
  /**
   * A versao da linha (backend/src/http/versao-da-ficha.ts). O formulario a
   * guarda AO ABRIR e a devolve no envio: o servidor recusa com 409
   * `ficha_alterada` se outra pessoa gravou depois (robos de 20/10/2026).
   */
  readonly versao?: string | null;
  readonly nome: string;
  readonly email: string | null;
  readonly ativo: boolean;
  readonly filial_id: string | null;
  readonly filial_nome: string | null;
  /** Se a pessoa já entra no sistema (tem credencial). Migração 0024. */
  readonly tem_acesso: boolean;
  /** Até quando vale o convite aberto; nulo quando não há convite aberto. */
  readonly convite_ate: string | null;
  /** O e-mail do convite aberto (0097); nulo quando ele não foi por e-mail. */
  readonly convite_envio: SituacaoDoEnvio | null;
  readonly convite_envio_tentativas: number | null;
  readonly convite_envio_erro: string | null;
  readonly convite_enviado_em: string | null;
  /**
   * 0128 · A trava que VALE para a pessoa: a união dos papéis dela que lançam
   * abastecimento, calculada pelo banco. Nula = nada travado — inclusive
   * quando um papel trava e outro, sem trava, libera.
   */
  readonly trava_do_lancamento?: { readonly tipos: readonly string[] | null; readonly tanques: readonly string[] | null } | null;
  /** 0134 · Conta de suporte: quem administra a plataforma e ajuda a empresa. A lista a escreve "Suporte". */
  readonly suporte?: boolean;
}

/** Em que pé está o e-mail do convite (0097). */
export type SituacaoDoEnvio = 'pendente' | 'enviado' | 'falhou' | 'cancelado';

/** O convite recém-emitido. O código aparece aqui e em nenhum outro lugar. */
export interface ConviteEmitido {
  readonly codigo: string;
  readonly expiraEm: string;
  /** Como ele chega à pessoa (0097): pelo e-mail do worker, ou pela mão de quem administra. */
  readonly entrega: 'email' | 'manual';
}

export interface PapelDaPessoa {
  readonly id: string;
  readonly usuario_id: string;
  readonly papel_id: string;
  readonly papel_nome: string;
  readonly papel_codigo: string;
  /**
   * O ALCANCE do papel: a empresa inteira (tudo nulo), uma filial (0081), uma
   * obra (0083) ou um contrato (0098) — nunca dois. O mesmo papel pode vir
   * mais de uma vez, um vinculo por alcance.
   */
  readonly filial_id?: string | null;
  readonly filial_nome?: string | null;
  readonly obra_id?: string | null;
  readonly obra_nome?: string | null;
  readonly contrato_id?: string | null;
  readonly contrato_codigo?: string | null;
  readonly contrato_nome?: string | null;
  /** A vigencia (0083), `aaaa-mm-dd`. Ausente no servidor de antes. */
  readonly inicio?: string | null;
  readonly fim?: string | null;
  /** O papel vale hoje (ativo e dentro da vigencia) — a conta e do servidor. */
  readonly vigente?: boolean;
  readonly concedido_em: string;
}

/** Os quatro alcances do papel (0098). */
export type TipoDeAlcance = 'empresa' | 'filial' | 'obra' | 'contrato' | 'fornecedor';

/** "Empresa inteira", "Só na filial Sinop", "Só na obra BR-163", "Só no contrato CTR-2026-01". */
export function alcanceEscrito(
  v: Pick<
    PapelDaPessoa,
    'filial_nome' | 'obra_nome' | 'contrato_codigo' | 'contrato_nome' | 'contrato_id' | 'obra_id' | 'filial_id'
  > & { readonly fornecedor_id?: string | null; readonly fornecedor_nome?: string | null }): string {
  if (v.fornecedor_id) return `Só no fornecedor ${v.fornecedor_nome ?? ''}`.trim();
  if (v.contrato_id) return `Só no contrato ${v.contrato_codigo ?? v.contrato_nome ?? ''}`.trim();
  if (v.obra_id) return `Só na obra ${v.obra_nome ?? ''}`.trim();
  if (v.filial_id) return `Só na filial ${v.filial_nome ?? ''}`.trim();
  return 'Empresa inteira';
}

/** O que cada alcance VÊ, na nota da concessão (0081, 0083, 0098). */
export const O_QUE_O_ALCANCE_VE: Record<TipoDeAlcance, string> = {
  empresa: 'O papel vale na empresa inteira: todas as máquinas, medições e custos.',
  filial: 'A pessoa vê as máquinas da filial e as que estão numa obra dela, e a medição inteira de cada contrato em que uma dessas máquinas está agora.',
  obra: 'A pessoa vê as máquinas alocadas na obra agora, com o histórico delas, e a medição inteira de cada contrato em que uma dessas máquinas está.',
  contrato: 'A pessoa vê as máquinas alocadas no contrato agora, com o histórico delas, e a medição e a receita do contrato inteiras, inclusive das máquinas que já saíram. Custo e margem ficam com quem vê a empresa inteira.',
  /* 0123 · o único alcance de gente de FORA da empresa, e por isso a frase diz
     o que ele NÃO vê — é isso que quem concede precisa conferir. */
  fornecedor: 'A pessoa vê os postos deste fornecedor, os abastecimentos lançados neles e a cobrança dele — e os códigos da frota, sem os quais não há o que escolher ao lançar. Não vê tanque, consumo, custo, ordem de abastecimento nem o que os outros postos lançaram.',
};

/** Os quatro assuntos do documento v6, e sao os do `check` da migracao. */
export const ASSUNTOS = ['compra', 'ajuste', 'ordem_servico', 'liberacao'] as const;

export type Assunto = (typeof ASSUNTOS)[number];

export const NOME_DO_ASSUNTO: Record<Assunto, string> = {
  compra: 'Compra',
  ajuste: 'Ajuste',
  ordem_servico: 'Ordem de serviço',
  liberacao: 'Liberação',
};

export interface Alcada {
  readonly id: string;
  readonly papel_id: string;
  readonly papel_nome: string;
  readonly assunto: Assunto;
  /** `numeric` chega como texto; nulo significa SEM TETO, e zero nao. */
  readonly limite_valor: string | null;
  readonly exige_segundo_aprovador: boolean;
}

export interface LinhaDaTrilha {
  readonly id: string;
  readonly acao: string;
  readonly recurso: string;
  readonly recurso_id: string | null;
  readonly resultado: 'concedido' | 'negado';
  readonly detalhe: string | null;
  readonly motivo: string | null;
  readonly ocorrido_em: string;
  readonly autor: string | null;
  readonly dados_antes: Record<string, unknown> | null;
  readonly dados_depois: Record<string, unknown> | null;
}

/**
 * O que cada acao da trilha quer dizer para quem le a tela. As quatro
 * primeiras sao as do filtro; as outras, as que o acesso escreve (convite,
 * troca de senha, suporte) — sem elas a linha aparecia com o nome interno.
 */
export const NOME_DA_ACAO: Record<string, string> = {
  insert: 'Cadastro',
  update: 'Alteração',
  delete: 'Exclusão',
  ler: 'Leitura',
  alterar: 'Alteração',
  emitir_convite: 'Convite emitido',
  aceitar_convite: 'Convite aceito',
  trocar_senha: 'Senha trocada',
  ativar_segundo_fator: 'Segundo fator ativado',
  desativar_segundo_fator: 'Segundo fator desativado',
  abrir_suporte: 'Acesso de suporte aberto',
};

export function situacaoDe(linha: { readonly ativo: boolean }): 'Em uso' | 'Arquivado' {
  return linha.ativo ? 'Em uso' : 'Arquivado';
}

/** `nome` em cinco dos seis cadastros, `descricao` no contrato. */
export function rotuloDe(linha: LinhaDeCadastro): string {
  return linha.nome ?? linha.descricao ?? linha.codigo;
}

/**
 * CPF e CNPJ sao guardados so com digitos — a mascara e a forma mais barata de
 * ter o mesmo cadastro duas vezes. Na LEITURA ela volta, porque e assim que o
 * numero se confere contra um documento em papel. A lista, o painel e a trilha
 * mostram o documento pela mesma funcao.
 */
export function formatarDocumento(digitos: string): string {
  if (digitos.length === 11) {
    return `${digitos.slice(0, 3)}.${digitos.slice(3, 6)}.${digitos.slice(6, 9)}-${digitos.slice(9)}`;
  }
  if (digitos.length === 14) {
    return `${digitos.slice(0, 2)}.${digitos.slice(2, 5)}.${digitos.slice(5, 8)}/${digitos.slice(8, 12)}-${digitos.slice(12)}`;
  }
  return digitos;
}
